<template>
  <CCard>
    <CCardHeader style="background-color: #145654;"
      class="border-bottom d-flex justify-content-between align-items-center">
      <h4 class="card-title mb-0 text-white">
        <CIcon name="cil-briefcase" size="xl" /> {{ $t('jobTypeMgmt.pageTitle') }}
      </h4>
      <CButton color="success" shape="pill" @click="openAddModal">
        <CIcon name="cil-plus" />
        &nbsp;{{ $t('jobTypeMgmt.addJobType') }}
      </CButton>
    </CCardHeader>

    <CCardBody>
      <div v-if="myDepartment">
        <h4 class="d-flex mb-3 justify-content-between align-items-center">
            {{ $t('staffMgmt.deptLabel') }} {{ myDepartment.departmentName }}
        </h4>

        <CDataTable :items="myDepartment.subCategories" :fields="subCategoryFields" hover :items-per-page="100"
          :no-footer="true">
          <template #slaHours="{ item }">
            <td class="text-center">{{ item.slaHours }}</td>
          </template>

          <template #actions="{ item }">
            <td class="text-center">
              <CButton size="sm" color="warning" shape="pill" @click="openEditModal(item)">
                <CIcon name="cil-pencil" />
              </CButton>
              <CButton size="sm" color="danger" shape="pill" class="ml-2" @click="deleteSubCategory(item)">
                <CIcon name="cil-trash" />
              </CButton>
            </td>
          </template>
        </CDataTable>
      </div>
      
      <div v-else class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <div class="mt-2">{{ $t('mgmt.loadingDept') }}</div>
      </div>

      <CModal :title="$t('jobTypeMgmt.addSubModal')" size="md" :show.sync="addModal">
        <div>
          <CInput :label="$t('jobTypeMgmt.jobTypeName')" :placeholder="$t('jobTypeMgmt.subNamePh')" v-model="newSubName" />
          <CInput :label="$t('jobTypeMgmt.slaHours')" type="number" :placeholder="$t('jobTypeMgmt.slaPh')" v-model.number="newSlaHours" />
        </div>

        <template #footer>
          <CButton color="secondary" @click="addModal = false">{{ $t('mgmt.cancel') }}</CButton>
          <CButton color="success" @click="addSubCategory">{{ $t('mgmt.add') }}</CButton>
        </template>
      </CModal>

      <CModal :title="$t('jobTypeMgmt.editSubModal')" size="md" :show.sync="editModal">
        <CModalBody v-if="editSubCategory">
          <CForm>
            <div>
              <CInput :label="$t('jobTypeMgmt.jobTypeName')" :placeholder="$t('jobTypeMgmt.subNamePh')" v-model="editSubCategory.name" />
              <CInput :label="$t('jobTypeMgmt.slaHours')" type="number" :placeholder="$t('jobTypeMgmt.slaPh')"
                v-model.number="editSubCategory.slaHours" />
            </div>
          </CForm>
        </CModalBody>
        <template #footer>
          <CButton color="secondary" @click="editModal = false">{{ $t('mgmt.cancel') }}</CButton>
          <CButton color="warning" @click="saveChanges">{{ $t('jobTypeMgmt.saveEditBtn') }}</CButton>
        </template>
      </CModal>
    </CCardBody>
  </CCard>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "JobTypeManagementSupervisor",
  data() {
    return {
      myDepartment: null, // เริ่มต้นเป็น null รอโหลดจาก API

      // Modal Controls
      editModal: false,
      addModal: false,

      // Data Models
      newSubName: "",
      newSlaHours: 1,
      editSubCategory: null,
    };
  },

  mounted() {
    this.fetchMyDepartment();
  },

  computed: {
    subCategoryFields() {
      return [
        { key: 'name', label: this.$t('jobTypeMgmt.jobTypeName'), _style: 'width: 60%' },
        { key: 'slaHours', label: this.$t('jobTypeMgmt.slaHours'), _style: "width:15%; text-align:center;", class: 'text-center' },
        { key: "actions", label: "", _style: 'width: 20%', class: 'text-center' },
      ];
    },
  },

  methods: {
    // 1. ดึงข้อมูลหน่วยงานของตัวเอง (Supervisor)
    async fetchMyDepartment() {
      try {
        // 1. ดึง Token ออกมาก่อน
        const token = localStorage.getItem("accessToken");

        // 2. แนบไปใน Header ของ Axios
        const res = await axios.get(`${process.env.VUE_APP_APP_URL}/category/supervisor`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        this.myDepartment = res.data.data;
      } catch (err) {
        console.error(err);
        Swal.fire(this.$t("mgmt.error"), this.$t("jobTypeMgmt.cannotLoadDept"), "error");
      }
    },

    openAddModal() {
      this.newSubName = "";
      this.newSlaHours = 1;
      this.addModal = true;
    },

    openEditModal(item) {
      // Clone Object เพื่อไม่ให้แก้ในตารางก่อนกดบันทึก
      this.editSubCategory = JSON.parse(JSON.stringify(item));
      this.editModal = true;
    },

    // 2. เพิ่มประเภทงาน (ใช้ Logic PUT แบบเดียวกับ Admin)
    async addSubCategory() {
      if (!this.newSubName.trim() || !this.newSlaHours) {
        Swal.fire(this.$t("mgmt.warn"), this.$t("jobTypeMgmt.warnComplete"), "warning");
        return;
      }

      try {
        // Clone ข้อมูลปัจจุบันมา
        const updatedCategory = JSON.parse(JSON.stringify(this.myDepartment));

        // Push ข้อมูลใหม่ลงไปใน Array
        updatedCategory.subCategories.push({
          name: this.newSubName,
          slaHours: parseInt(this.newSlaHours)
        });

        // ส่งข้อมูลทั้งก้อนไปอัปเดต (ใช้ Service ตัวเดียวกับ Admin ที่เราทำไว้)
        await axios.put(`${process.env.VUE_APP_APP_URL}/category`, updatedCategory);

        Swal.fire(this.$t("mgmt.success"), this.$t("jobTypeMgmt.addSubOk"), "success");
        this.addModal = false;
        this.fetchMyDepartment(); // โหลดข้อมูลใหม่

      } catch (err) {
        Swal.fire(this.$t("mgmt.error"), err.response?.data?.message || this.$t("mgmt.addFail"), "error");
      }
    },

    // 3. บันทึกการแก้ไข
    async saveChanges() {
      if (!this.editSubCategory.name.trim() || !this.editSubCategory.slaHours) {
        Swal.fire(this.$t("mgmt.warn"), this.$t("jobTypeMgmt.warnComplete"), "warning");
        return;
      }

      try {
        const updatedCategory = JSON.parse(JSON.stringify(this.myDepartment));

        // หา Index ของตัวที่จะแก้ (ใช้ _id เป็นตัวเทียบ)
        const index = updatedCategory.subCategories.findIndex(
          sub => sub._id === this.editSubCategory._id
        );

        if (index !== -1) {
          updatedCategory.subCategories[index] = this.editSubCategory;
        }

        // ส่ง PUT ไปอัปเดต
        await axios.put(`${process.env.VUE_APP_APP_URL}/category`, updatedCategory);

        Swal.fire(this.$t("mgmt.success"), this.$t("mgmt.saveSaved"), "success");
        this.editModal = false;
        this.fetchMyDepartment();

      } catch (err) {
        Swal.fire(this.$t("mgmt.error"), err.response?.data?.message || this.$t("mgmt.editFail"), "error");
      }
    },

    // 4. ลบประเภทงาน
    async deleteSubCategory(item) {
      Swal.fire({
        title: this.$t("jobTypeMgmt.delSubTitle"),
        text: this.$t("jobTypeMgmt.delSubLine").replace("{name}", item.name),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: this.$t("mgmt.delete"),
        cancelButtonText: this.$t("mgmt.cancel")
      }).then(async (result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: this.$t("mgmt.sureDelete"),
            text: this.$t("jobTypeMgmt.subWillDeleteLine").replace("{name}", item.name),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: this.$t("mgmt.confirmDelete"),
            cancelButtonText: this.$t("mgmt.cancel")
          }).then(async (result2) => {
            if (result2.isConfirmed) {
              try {
                const updatedCategory = JSON.parse(JSON.stringify(this.myDepartment));

                // กรองเอาตัวที่ไม่ใช่ออก (ลบตัวที่เลือก)
                updatedCategory.subCategories = updatedCategory.subCategories.filter(
                  sub => sub._id !== item._id
                );

                // ส่ง PUT ไปอัปเดต
                await axios.put(`${process.env.VUE_APP_APP_URL}/category`, updatedCategory);

                Swal.fire(this.$t("mgmt.success"), this.$t("jobTypeMgmt.delSubOk"), "success");
                this.fetchMyDepartment();
              } catch (err) {
                Swal.fire(this.$t("mgmt.error"), err.response?.data?.message || this.$t("mgmt.deleteFail"), "error");
              }
            }
          })
        }
      })
    },
  },
};
</script>

<style scoped>
ul {
  list-style-type: disc;
}

.fixed-table {
  table-layout: fixed;
  width: 100%;
}

.fixed-table th,
.fixed-table td {
  text-align: left;
  vertical-align: middle;
  word-wrap: break-word;
}

.col-70 {
  width: 70%;
}

.col-30 {
  width: 30%;
}

.col-20 {
  width: 20%;
}

.col-10 {
  width: 10%;
}
</style>
